import { Rules, SecretInformation } from '@gamepark/rules-api'
import Card, { CardInLine } from '../../Card'
import Color from '../../Color'
import Game from '../../Game'
import {
  getBeforeAndAfterCards,
  getCardAtLocation,
  getCardVisibleOnPointer,
  getPossiblePlaces,
} from '../../Line'
import Move, { MoveType } from '../../moves/Move'
import OriflammeCardsRules from '../OriflammeCardsRules'

/**
 * This class implements the rules of the board game.
 * It must follow Game Park "Rules" API so that the Game Park server can enforce the rules.
 */
export default class SpyRules extends OriflammeCardsRules {
  getTargets(): CardInLine[] {
    return getBeforeAndAfterCards(this.state.cards, this.state.pointer)
  }

  /**
   * Return the exhaustive list of moves that can be played.
   * This is used for 2 features:
   * - security (preventing unauthorized moves from being played);
   * - "Dummy players": when a player leaves a game, it is replaced by a "Dummy" that plays random moves, allowing the other players to finish the game.
   * If the game allows a very large (or infinite) number of moves, instead of implementing this method, you can implement instead:
   * - isLegal(move: Move):boolean, for security; and
   * - A class that implements "Dummy" to provide a custom Dummy player.
   */
  getLegalMoves(playerId: Color): Move[] {
    //if it's not player in arg turn : return []
    if (this.getActivePlayer() !== playerId) return []

    const stealableColors = this.state.players.filter(player=> player.id != playerId && player.points>0).map(player=>player.id)

    const targets = this.getTargets().filter(target=>stealableColors.includes(target.color))
    
    return targets.map((target) => {
      return { type: MoveType.steal, target: target.location, to: playerId }
    })
  }

  /**
   * This is the one and only play where you will update the game's state, depending on the move that has been played.
   *
   * @param move The move that should be applied to current state.
   */
  play(move: Move): Move[] {
    switch (move.type) {
      case MoveType.steal:
        console.log('steal', move)
        return [
          {
            type: MoveType.getTokens,
            to: move.to,
            from: getCardAtLocation(this.state.cards, move.target)!.color,
            nb: 1,
          },
          { type: MoveType.movePointer },
        ]
      default:
        break
    }

    return super.play(move)
  }

  /**
   * Here you can return the moves that should be automatically played when the game is in a specific state.
   * Here is an example from monopoly: you roll a dice, then move you pawn accordingly.
   * A first solution would be to do both state updates at once, in a "complex move" (RollDiceAndMovePawn).
   * However, this first solution won't allow you to animate step by step what happened: the roll, then the pawn movement.
   * "getAutomaticMoves" is the solution to trigger multiple moves in a single action, and still allow for step by step animations.
   * => in that case, "RollDice" could set "pawnMovement = x" somewhere in the game state. Then getAutomaticMove will return "MovePawn" when
   * "pawnMovement" is defined in the state.
   * Of course, you must return nothing once all the consequences triggered by a decision are completed.
   * VERY IMPORTANT: you should never change the game state in here. Indeed, getAutomaticMove will never be called in replays, for example.
   *
   * @return The next automatic consequence that should be played in current game state.
   */
  getAutomaticMoves(): Move[] {
    return super.getAutomaticMoves()
  }
}
